import { AddIcon, CheckIcon, EditIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Heading,
  IconButton,
  Select,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { useState } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { franchiseMapIndex, titleMapIndex } from "../../../core/constants";
import { getAdminReadingOrders } from "../../../services/adminApi";
import Spinner from "../../../shared/components/Spinner";
import { Card } from "../../auth/components/Login/Card";
import CommunityAdmin from "../../community/routes/CommunityAdmin";
import ComicsList from "./ComicsList";
import Corrections from "../components/Corrections";
import RawScraps from "../components/RawScraps";
import Requests from "../components/Requests";
import Support from "../components/Support";

// Tabs that can be opened from a link, e.g. the support notification email:
// /reading-orders/all?tab=support&conversation=12
const TAB_PARAMS = { support: 6 };

const ReadingOrderList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("gray.50", "inherit");
  const searchParams = new URLSearchParams(location.search);
  const linkedConversationId = Number(searchParams.get("conversation")) || null;
  const [tabIndex, setTabIndex] = useState(
    location.state?.adminTab ?? TAB_PARAMS[searchParams.get("tab")] ?? 0
  );
  const [sort, setSort] = useState({ column: "id", direction: "desc" });
  const [filterBy, setFilterBy] = useState({
    franchise: "",
    type: "",
    status: "",
  });

  const { data, isSuccess, isLoading, isError } = useQuery(
    [
      "readingOrders",
      filterBy.type,
      filterBy.franchise,
      filterBy.published,
      sort.column,
      sort.direction,
    ],
    () =>
      getAdminReadingOrders(
        filterBy.type,
        filterBy.franchise,
        filterBy.published,
        sort.column,
        sort.direction
      )
  );

  const handleSort = (column) => {
    if (sort.column === column) {
      setSort((prevSort) => ({
        column,
        direction: prevSort.direction === "asc" ? "desc" : "asc",
      }));
    } else {
      setSort({ column, direction: "asc" });
    }
  };

  const handleFilter = (filterType, value) => {
    setFilterBy((prevFilterBy) => ({
      ...prevFilterBy,
      [filterType]: value,
    }));
  };

  const onEditInfo = (readingOrderId) => {
    navigate(`/reading-orders/${readingOrderId}/edit`);
  };

  const onEditOrder = (readingOrderId) => {
    navigate(`/reading-orders/${readingOrderId}/comics`);
  };

  const onPublish = (readingOrderId) => {
    navigate(`/reading-orders/${readingOrderId}/publish`);
  };

  const tabAction = {
    0: {
      label: "Add new reading order",
      onClick: () => navigate("/reading-orders/new"),
    },
    1: {
      label: "Add a new comic",
      onClick: () => navigate("/comics/new"),
    },
    3: {
      label: "New book club",
      onClick: () => navigate("/bookclub/admin/book-clubs/new"),
    },
  }[tabIndex];

  return (
    <Box bg={bgColor} minH="100vh" py="12" px={{ base: "4", lg: "8" }}>
      <Box maxW="xxl" mx="auto">
        <Card>
          <Center>
            <Heading as="h2" my="6">
              Admin
            </Heading>
            {!!tabAction && (
              <Button
                onClick={tabAction.onClick}
                ml="auto"
                leftIcon={<AddIcon />}
              >
                {tabAction.label}
              </Button>
            )}
          </Center>
          <Tabs
            index={tabIndex}
            isLazy
            onChange={(index) => setTabIndex(index)}
          >
            <TabList>
              <Tab>Reading orders</Tab>
              <Tab>Comics</Tab>
              <Tab>Requests</Tab>
              <Tab>Book Club</Tab>
              <Tab>Corrections</Tab>
              <Tab>Raw scraps</Tab>
              <Tab>Support</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px="0" pt="6">
                <Flex mb={4}>
                  <Center minW={100}>
                    <p>Filter by</p>
                  </Center>
                  <Select
                    onChange={(e) => handleFilter("franchise", e.target.value)}
                    value={filterBy.franchise}
                    mr={4}
                  >
                    <option value="">All franchises</option>
                    {Object.keys(franchiseMapIndex).map((franchise) => (
                      <option value={franchise} key={franchise}>
                        {franchiseMapIndex[franchise]}
                      </option>
                    ))}
                  </Select>
                  <Select
                    onChange={(e) => handleFilter("type", e.target.value)}
                    value={filterBy.type}
                    mr={4}
                  >
                    <option value="">All types</option>
                    {Object.keys(titleMapIndex).map((type) => (
                      <option key={type} value={type}>
                        {titleMapIndex[type]}
                      </option>
                    ))}
                  </Select>
                  <Select
                    onChange={(e) => handleFilter("published", e.target.value)}
                    value={filterBy.published}
                    mr={4}
                  >
                    <option value="">All Statuses</option>
                    <option value="0">Draft</option>
                    <option value="1">Published</option>
                  </Select>
                </Flex>
                <Divider />
                {isError && <p>Error.</p>}
                {isLoading && <Spinner />}
                {isSuccess && !isLoading && (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th onClick={() => handleSort("id")}>
                          ID{" "}
                          {sort.column === "id" && (
                            <span>
                              {sort.direction === "asc" ? " ▼" : " ▲"}
                            </span>
                          )}
                        </Th>
                        <Th onClick={() => handleSort("title")}>
                          Title{" "}
                          {sort.column === "title" && (
                            <span>
                              {sort.direction === "asc" ? " ▼" : " ▲"}
                            </span>
                          )}
                        </Th>
                        <Th onClick={() => handleSort("comicsCount")}>
                          Comics Count{" "}
                          {sort.column === "comicsCount" && (
                            <span>
                              {sort.direction === "asc" ? " ▼" : " ▲"}
                            </span>
                          )}
                        </Th>
                        <Th onClick={() => handleSort("publushed")}>
                          Status{" "}
                          {sort.column === "published" && (
                            <span>
                              {sort.direction === "asc" ? " ▼" : " ▲"}
                            </span>
                          )}
                        </Th>
                        <Th onClick={() => handleSort("franchise")}>
                          Franchise{" "}
                          {sort.column === "franchise" && (
                            <span>
                              {sort.direction === "asc" ? " ▼" : " ▲"}
                            </span>
                          )}
                        </Th>
                        <Th onClick={() => handleSort("type")}>
                          Type{" "}
                          {sort.column === "type" && (
                            <span>
                              {sort.direction === "asc" ? " ▼" : " ▲"}
                            </span>
                          )}
                        </Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data.data.map((order) => (
                        <Tr key={order.id}>
                          <Td>{order.id}</Td>
                          <Td>
                            {order.title} {order.years && `(${order.years})`}
                          </Td>
                          <Td>{order.comicsCount}</Td>
                          <Td>
                            {order.published
                              ? "Published"
                              : order.scheduledFor
                              ? `Scheduled for ${order.scheduledFor}`
                              : "Draft"}
                          </Td>
                          <Td>{franchiseMapIndex[order.franchise]}</Td>
                          <Td>{titleMapIndex[order.type]}</Td>
                          <Td>
                            <Flex>
                              <Spacer />
                              <Tooltip hasArrow label="Edit order">
                                <IconButton
                                  aria-label="Edit Order"
                                  icon={<HamburgerIcon />}
                                  onClick={() => onEditOrder(order.id)}
                                />
                              </Tooltip>
                              <Tooltip hasArrow label="Edit info">
                                <IconButton
                                  aria-label="Edit Info"
                                  icon={<EditIcon />}
                                  onClick={() => onEditInfo(order.id)}
                                />
                              </Tooltip>
                              <Tooltip hasArrow label="Publish order">
                                <IconButton
                                  aria-label="Publish order"
                                  icon={<CheckIcon />}
                                  onClick={() =>
                                    onPublish(order.id, order.published)
                                  }
                                  disabled={order.published}
                                />
                              </Tooltip>
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </TabPanel>
              <TabPanel px="0" pt="6">
                <ComicsList embedded />
              </TabPanel>
              <TabPanel px="0" pt="6">
                <Requests />
              </TabPanel>
              <TabPanel px="0" pt="6">
                <CommunityAdmin embedded />
              </TabPanel>
              <TabPanel px="0" pt="6">
                <Corrections />
              </TabPanel>
              <TabPanel px="0" pt="6">
                <RawScraps />
              </TabPanel>
              <TabPanel px="0" pt="6">
                <Support initialConversationId={linkedConversationId} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>
      </Box>
    </Box>
  );
};

export default ReadingOrderList;
